import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import Konva from 'konva';

declare const jsPDF: any;

function getModelsControls(models: ICrossSectionModel[], logpoints: { modelId: number }[], pipes: { modelId: number }[], modelNamesMap: Record<number, string>) {
    const modelIds = models.map(m => m.modelId);
    const logpointIds = logpoints.map(l => l.modelId);
    const pipeIds = pipes.map(p => p.modelId);

    return Array.from(new Set([...modelIds, ...logpointIds, ...pipeIds])).map(modelId => {
        const serverModel = models.find(m => m.modelId == modelId);

        return {
            modelId,
            name: (modelNamesMap[modelId] ?? 'No model') + codesString(serverModel),
            color: serverModel?.color,
            isModel: modelIds.includes(modelId),
            isLogpoint: logpointIds.includes(modelId),
            isPipe: pipeIds.includes(modelId),
            isPointCloud: false,

            children: (serverModel?.lines || []).filter(Boolean).map(line => ({
                modelId: line.id,
                name: line.name + codesString(line),
                color: line.color ?? serverModel?.color,
                isModel: true,
            })),
        };
    });
}

function getPointCloudControls(pointClouds: { pointcloudId: number }[], pointcloudNamesMap: Record<number, string>) {
    return pointClouds.map(pc => ({
        modelId: pc.pointcloudId,
        name: pointcloudNamesMap[pc.pointcloudId] ?? `Point Cloud ${pc.pointcloudId}`,
        color: 'rgb(0, 0, 0)',
        isModel: false,
        isLogpoint: false,
        isPipe: false,
        isPointCloud: true,
        children: [],
    }));
}

function codesString(item: { terrainCode?: string; surfaceCode?: string }) {
    if (!item) return '';

    const codes = [
        item.terrainCode ? `${__('js_files.layer.terrain_code')}: ${ProjectCodesService.codeWithDescription(item.terrainCode)}` : null,
        item.surfaceCode ? `${__('js_files.layer.surface_code')}: ${ProjectCodesService.codeWithDescription(item.surfaceCode)}` : null,
    ].filter(Boolean);
    return codes.length ? ` (${codes.join(', ')})` : '';
}

const createPdf = async (filename: string, stage: Konva.Stage, modelControls: any) => {
    return new Promise<void>(done => {
        jQuery.getScript('//web.infrakit.com/lib/jspdf/dist/jspdf.min.js', () => {
            const pdf = new jsPDF('landscape'); // a4, 210 x 297 mm

            let allModelsCount = 0;
            modelControls.forEach(model => {
                allModelsCount += 1 + model.children.length;
            });

            const jpgData = stage.toDataURL({
                mimeType: 'image/png',
                width: stage.width(),
                height: stage.height(),
                quality: 1,
            });
            const r = stage.width() / stage.height();
            const paperWidth = 285;
            let ycursor = paperWidth / r + 20 + 5;

            pdf.addImage(jpgData, 'JPEG', 5, 20, paperWidth, paperWidth / r);

            pdf.setFontSize(6);

            const checkOverflow = () => {
                if (ycursor > 200) {
                    pdf.addPage();
                    ycursor = 10;
                }
            };

            modelControls.forEach(model => {
                checkOverflow();
                const rgb = model.color ? model.color.split(',') : [0, 0, 0];
                pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
                pdf.text(10, ycursor, model.name);
                ycursor += 3;
                console.log('yCursor:', ycursor, model.name);

                (model.children ?? []).forEach(child => {
                    checkOverflow();
                    const childRgb = child.color ? child.color.split(',') : rgb;
                    pdf.setTextColor(childRgb[0], childRgb[1], childRgb[2]);
                    pdf.text(12, ycursor, child.name);
                    ycursor += 3;
                });
            });
            pdf.save(filename);
            done();
        });
    });
};

const getUrlParams = () => {
    const raw = new URLSearchParams(window.location.search);
    const alignmentId = raw.get('alignmentId') ? parseInt(raw.get('alignmentId'), 10) : null;

    return {
        alignmentId,
        alignmentIndex: raw.get('alignmentIndex') ? parseInt(raw.get('alignmentIndex'), 10) : 0,
        station: raw.get('station') ? parseFloat(raw.get('station')) : null,
        stationStart: raw.get('stationStart') ? parseFloat(raw.get('stationStart')) : null,
        stationEnd: raw.get('stationEnd') ? parseFloat(raw.get('stationEnd')) : null,
        leftBorder: raw.get('leftBorder') ? parseFloat(raw.get('leftBorder')) : null,
        rightBorder: raw.get('rightBorder') ? parseFloat(raw.get('rightBorder')) : null,
        lat1: raw.get('lat1') ? parseFloat(raw.get('lat1')) : null,
        lon1: raw.get('lon1') ? parseFloat(raw.get('lon1')) : null,
        lat2: raw.get('lat2') ? parseFloat(raw.get('lat2')) : null,
        lon2: raw.get('lon2') ? parseFloat(raw.get('lon2')) : null,
        selectedLogpointId: raw.get('selectedLogpointId') ? parseFloat(raw.get('selectedLogpointId')) : null,
        includeModelIds: raw.get('includeModelIds') ? raw.get('includeModelIds')?.split(',').map(parseFloat) : [alignmentId],
        clearCache: raw.get('clearCache') === 'true',
    };
};

const getEmbeddedParams = () => {
    let alignmentId = (document.querySelector('input[name=alignmentId]') as HTMLInputElement)?.value as any;
    if (alignmentId) {
        alignmentId = parseFloat(alignmentId);
    }
    const includeModelIds = (document.querySelector('input[name=includeModelIds]') as HTMLInputElement)?.value;

    // modelId, folderId, selectedLogpointId, selectedLogpoints, selectedPipes, selectedPipeId, selectedModels, leftBorder, unselectedModels
    return {
        alignmentId,
        includeModelIds: includeModelIds ? includeModelIds.split(',').map(parseFloat) : [alignmentId],
    };
};

export const CrossSectionUtils = {
    getModelsControls,
    getPointCloudControls,
    createPdf,
    getUrlParams,
    codesString,
};
